import { useAppDispatch, useAppParams, useAppSelector } from "@app/hooks";
import { clearProject, selectCurrentProject, setProject } from "@features/project/projectSlice";
import { useContextAwareGetProjectById } from "@hooks/index";
import { useEffect } from "react";
import { clearTemporaryMapData } from "@features/project/projectSlice";
import useErrorMessage from "@utils/useErrorMessage";

/**
 * Handles project state management across different route contexts:
 * - In /project routes: Uses projectId from URL params to fetch project data
 * - In /builder routes: Extracts project ID from activity data to fetch project data
 * This ensures project data is available and properly refetched when needed in both contexts.
 */
export const useHandleProjectState = () => {
  const dispatch = useAppDispatch();
  const { projectId } = useAppParams();
  const { project, activity } = useAppSelector(selectCurrentProject);

  const isNewProject = projectId?.toLowerCase() === "new";

  const id = projectId ?? activity?.project?.toString() ?? "";
  const shouldFetchProject = id && !isNewProject;
  const { data: projectData, isLoading, isError, error, isFetching } = useContextAwareGetProjectById(id, { skip: !shouldFetchProject });
  const { errorMsg } = useErrorMessage({ error, isError })

  useEffect(() => {
    if (projectData && shouldFetchProject && id && !isNaN(+id)) {
      dispatch(setProject(projectData));
      dispatch(clearTemporaryMapData());
    }
  }, [projectData, dispatch, shouldFetchProject]);

  useEffect(() => {
    if (isNewProject) {
      dispatch(clearProject());
      dispatch(clearTemporaryMapData());
    }
  }, [isNewProject, dispatch])

  return { project, projectId, isLoading, errorMsg, isNewProject, isFetching };
}
